import { Link } from 'react-router-dom';
import { ArrowRightSvg } from '~/svg';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { fetchRecentCampaigns, fetchStates } from '~/services/campaign';
import { formatDate, parseISO } from 'date-fns';
import { formatCurrency } from '~/common/form/Inputs';
import { CampaignListItem } from '~/types/campaigns';
import { MdCheck } from 'react-icons/md';
import { useState } from 'react';

type State = {
    id: number;
    state: string;
    abbreviation: string;
};

const columnHelper = createColumnHelper<CampaignListItem>();

const columns = [
    columnHelper.accessor('full_name', {
        header: () => 'Client',
        cell: (props) => {
            const patient_info = props.row.original.patient_info;

            return (
                <>
                    <Link
                        className='text-primary'
                        to={`/campaigns/${props.row.original.campaign_id}`}
                    >
                        {props.renderValue()}
                    </Link>
                    <div className={'text-[12px]/[16px] text-[#949599]'}>
                        {patient_info?.location}
                    </div>
                </>
            );
        },
    }),
    columnHelper.accessor((row) => row.patient_info?.medical_issue ?? '', {
        id: 'medical_issue',
        header: () => 'Condition',
        cell: (info) => info.getValue(),
    }),
    columnHelper.accessor(
        (props) =>
            props.funded_date
                ? formatDate(parseISO(props.funded_date), 'MM/dd/yy')
                : '',
        {
            id: 'funded_date',
            header: () => 'Open Date',
            cell: (props) => props.renderValue(),
        },
    ),
    columnHelper.accessor('disbursable_date', {
        id: 'disbursable',
        header: () => 'Disbursable',
        cell: (info) =>
            info.getValue() ? (
                <MdCheck className='text-primary' size={20} />
            ) : (
                ''
            ),
    }),
    columnHelper.accessor('unique_donor_count', {
        id: 'unique_donors',
        header: () => 'Donors',
        cell: (props) => props.renderValue(),
    }),
    columnHelper.accessor('entire_funds_raised', {
        id: 'entire_funds_raised',
        header: () => 'Donations',
        cell: (props) => {
            const val = props.getValue();
            const num = parseFloat(val);
            return formatCurrency(num);
        },
    }),
];

const NewlyOpenedCampaigns = () => {
    const [selectedState, setSelectedState] = useState<string>('');

    // Fetch states for dropdown
    const { data: states } = useQuery({
        queryKey: ['states'],
        queryFn: fetchStates,
        staleTime: Infinity,
        gcTime: Infinity,
    });

    const { data: recentCampaigns, isLoading } = useQuery({
        queryKey: ['newly-opened-campaigns', selectedState],
        queryFn: async () => {
            const response = await fetchRecentCampaigns(
                selectedState || undefined,
            );
            return response;
        },
        staleTime: Infinity,
        gcTime: Infinity,
    });

    const table = useReactTable({
        data: recentCampaigns || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <section className='data-table-common h-full rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark'>
            <div className='flex flex-col gap-3 p-4 pb-2 sm:flex-row sm:items-center sm:justify-between md:p-6 md:pb-2 xl:p-7.5 xl:pb-2'>
                <h2 className='text-xl font-semibold text-black dark:text-white'>
                    Newly Opened Campaigns
                </h2>

                <div className='flex items-center gap-3'>
                    <select
                        value={selectedState}
                        onChange={(e) => setSelectedState(e.target.value)}
                        className='rounded border border-stroke bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark'
                    >
                        <option value=''>All States</option>
                        {states?.map((state: State) => (
                            <option key={state.id} value={state.abbreviation}>
                                {state.state}
                            </option>
                        ))}
                    </select>
                    <Link
                        to='/new-campaigns'
                        className='flex items-center gap-2 text-primary'
                    >
                        View All <ArrowRightSvg />
                    </Link>
                </div>
            </div>

            <div className='overflow-x-auto'>
                <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                    <thead className='border-separate px-4'>
                        {isLoading
                            ? Array.from({ length: 5 }).map((_, index) => (
                                  <tr key={index}>
                                      {columns.map((_column, colIndex) => (
                                          <td
                                              key={colIndex}
                                              className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                          >
                                              <div className='max-w-sm animate-pulse'>
                                                  <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      ))}
                                  </tr>
                              ))
                            : table.getHeaderGroups().map((headerGroup) => (
                                  <tr
                                      style={{ fontSize: '14px' }}
                                      key={headerGroup.id}
                                  >
                                      {headerGroup.headers.map((header) => (
                                          <th
                                              className='text-secondary'
                                              style={{
                                                  paddingTop: '16px',
                                                  paddingBottom: '16px',
                                              }}
                                              key={header.id}
                                          >
                                              {header.isPlaceholder
                                                  ? null
                                                  : flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext(),
                                                    )}
                                          </th>
                                      ))}
                                  </tr>
                              ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr className='border-t border-stroke' key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <td
                                        style={{
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                        key={cell.id}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default NewlyOpenedCampaigns;
